import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useForm, useFormState, useWatch, FormProvider } from 'react-hook-form';
import { useToast } from '../../components/shared/Toast';
import LoadingSkeleton from '../../components/shared/LoadingSkeleton';
import { useConfirmModal } from '../../components/shared/ConfirmModal';
import { PAGE_HEADER_SLOT_ID } from '../../layouts/AppLayout';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Alert, Badge, Box, Button, Card, CardContent, CardHeader, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Link, List,
  ListItem, ListItemAvatar, ListItemText, Popover, Portal, Stack, Tab, Tabs,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CalculateIcon from '@mui/icons-material/Calculate';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import ShareIcon from '@mui/icons-material/Share';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PersonIcon from '@mui/icons-material/Person';
import WorkIcon from '@mui/icons-material/Work';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import CreditScoreIcon from '@mui/icons-material/CreditScore';
import SavingsIcon from '@mui/icons-material/Savings';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import DescriptionIcon from '@mui/icons-material/Description';
import ChecklistIcon from '@mui/icons-material/Checklist';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ViewTimelineIcon from '@mui/icons-material/ViewTimeline';
import TimelineIcon from '@mui/icons-material/Timeline';
import ChatIcon from '@mui/icons-material/Chat';
import CompareIcon from '@mui/icons-material/Compare';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import GavelIcon from '@mui/icons-material/Gavel';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import DatasetLinkedIcon from '@mui/icons-material/DatasetLinked';
import { mockActivities, mockChatMessages, mockChatThreads } from '../../services/mockData';
import configurationApi from '../../services/configurationApi';
import useLockDeskStatus from '../../hooks/useLockDeskStatus';
import loansApi from '../../services/loansApi';
import pricingApi, { runPricingWithEngine } from '../../services/pricingApi';
import { useLazyGetCountyLoanLimitQuery } from '../../services/countyLoanLimitApi';
import {
  appliesCountyLoanLimit,
  evaluateCountyLoanLimits,
  fetchCountyLimitsForPricing,
  getLoanPropertyInfo,
  resolveMortgageTypeToPricingFamily,
  applyRepresentativeFicoToPricingLoan,
} from '../../services/pricingEngineService';
import { env } from '../../config/env';
import StatusBadge, { LOAN_STATUS_MAP } from '../../components/shared/StatusBadge';
import SectionHeader from '../../components/shared/SectionHeader';
import DualAUSComponent from './components/DualAUSComponent';
import FeesWorksheetComponent from './components/FeesWorksheetComponent';
import AIChatPanel from '../../components/shared/AIChatPanel';
import COCRequestForm from './components/COCRequestForm';
import PostLockActionsTab from './components/PostLockActionsTab';
import COCRequestList from './components/COCRequestList';
import COCRequestDetail from './components/COCRequestDetail';
import BorrowerInfoTab from './components/BorrowerInfoTab';
import IncomeEmploymentTab from './components/IncomeEmploymentTab';
import PropertyTab from './components/PropertyTab';
import LoanTab from './components/LoanTab';
import CreditLiabilitiesTab from './components/CreditLiabilitiesTab';
import AssetsTab from './components/AssetsTab';
import ConditionsTab from './components/ConditionsTab';
import DocumentsTab from './components/DocumentsTab';
import PricingTab from './components/PricingTab';
import GovernmentTab from './components/GovernmentTab';
import LoanVelocityTiles from './components/LoanVelocityTiles';
import KeyDatesCard from './components/KeyDatesCard';
import LoanTimelineTab from './components/LoanTimelineTab';
import DocOrderTab from './components/DocOrderTab';
import NonQmTab from './components/NonQmTab';
import {
  NON_QM_MORTGAGE_TYPE_VALUE,
  VESTA_BASE_MORTGAGE_TYPE_FOR_NON_QM,
  getEffectiveMortgageType,
  isNonQmIndicatorTrue,
  normalizeNonQmFormSnapshot,
} from '../../utils/nonQm';
import { getAxiosErrorMessage } from '../../utils/getAxiosErrorMessage';
import { keyDatesArrayToObject } from '../../services/keyDatesMapper';
import { shouldShowDocOrderTab } from '../../utils/loanDocOrderGates';
import { fmtDollar, fmtPct, fmtPricingPct } from '../../utils/formatters';
import { BORROWER_PHONE_TYPES } from '../../utils/borrowerPhones';
import { getDisplayTotalMonthlyPayment } from '../../utils/monthlyPaymentDisplay';
import { fetchFredMortgageRate } from '../../services/fredRateService';
import { authService } from '../../services/authService';
import { isBrokerReadOnlyRole } from '../../config/brokerAccess';
import { getHomeRoute } from '../../config/roles';
import {
  PullCreditModal,
  OrderAppraisalModal,
  OpenTitleModal,
  OrderVOAModal,
  OrderVOIEModal
} from './components/SelfServiceModals';

const GOVERNMENT_MORTGAGE_TYPES = ['FHA', 'VA', 'USDA'];

/** Deep-clone GET /loans payload for the form; converts govt financed flags to Yes/No for selects. */
function buildFormSnapshotFromLoanDetail(detail) {
  if (!detail) return {};
  const snapshot = JSON.parse(JSON.stringify(detail));
  if (!snapshot.nonQm || typeof snapshot.nonQm !== 'object') snapshot.nonQm = {};
  snapshot.nonQm = normalizeNonQmFormSnapshot(snapshot.nonQm);
  const loan = snapshot.loan;
  if (loan) {
    if (loan.escrowPropertyTaxesWaiverIndicator === undefined && loan.EscrowPropertyTaxesWaiverIndicator !== undefined) {
      loan.escrowPropertyTaxesWaiverIndicator = loan.EscrowPropertyTaxesWaiverIndicator;
    }
    if (loan.escrowPropertyInsuranceExFloodWaiverIndicator === undefined && loan.EscrowPropertyInsuranceExFloodWaiverIndicator !== undefined) {
      loan.escrowPropertyInsuranceExFloodWaiverIndicator = loan.EscrowPropertyInsuranceExFloodWaiverIndicator;
    }
    if (loan.refinanceCashOutDeterminationType === undefined && loan.RefinanceCashOutDeterminationType !== undefined) {
      loan.refinanceCashOutDeterminationType = loan.RefinanceCashOutDeterminationType;
    }
    if (loan.cashOutAmount === undefined && loan.CashOutAmount !== undefined) {
      loan.cashOutAmount = loan.CashOutAmount;
    }
    if (loan.monthsOfReservesAvailable === undefined && loan.MonthsOfReservesAvailable !== undefined) {
      loan.monthsOfReservesAvailable = loan.MonthsOfReservesAvailable;
    }
    if (loan.monthsOfReservesAvailableEstimate === undefined && loan.MonthsOfReservesAvailableEstimate !== undefined) {
      loan.monthsOfReservesAvailableEstimate = loan.MonthsOfReservesAvailableEstimate;
    }
    // Legacy single-field cash-out refi → Refinance + determination (Vesta does not use loanPurpose = CashOut)
    if (loan.loanPurpose === 'CashOutRefinance') {
      loan.loanPurpose = 'Refinance';
      if (!loan.refinanceCashOutDeterminationType) {
        loan.refinanceCashOutDeterminationType = 'CashOut';
      }
    }
    const nqmRaw = loan.nonQualifiedMortgageIndicator ?? loan.NonQualifiedMortgageIndicator;
    if (nqmRaw !== undefined) loan.nonQualifiedMortgageIndicator = nqmRaw;
    delete loan.NonQualifiedMortgageIndicator;
  }
  if (snapshot.nonQm.nonQualifiedMortgageIndicator === undefined && snapshot.loan?.nonQualifiedMortgageIndicator !== undefined) {
    snapshot.nonQm.nonQualifiedMortgageIndicator = snapshot.loan.nonQualifiedMortgageIndicator;
  }
  if (snapshot.loan?.nonQualifiedMortgageIndicator === undefined && snapshot.nonQm.nonQualifiedMortgageIndicator !== undefined) {
    snapshot.loan.nonQualifiedMortgageIndicator = snapshot.nonQm.nonQualifiedMortgageIndicator;
  }
  // Surface the UI-only "Non-QM" mortgage type so the Loan tab dropdown reflects the indicator.
  if (snapshot.loan && snapshot.loan.nonQualifiedMortgageIndicator === true) {
    snapshot.loan.mortgageType = NON_QM_MORTGAGE_TYPE_VALUE;
  }
  const govt = snapshot.governmentLoanInfo;
  if (govt?.fha && typeof govt.fha.upfrontMipFinanced === 'boolean')
    govt.fha.upfrontMipFinanced = govt.fha.upfrontMipFinanced ? 'Yes' : 'No';
  if (govt?.va && typeof govt.va.vaFundingFeeFinanced === 'boolean')
    govt.va.vaFundingFeeFinanced = govt.va.vaFundingFeeFinanced ? 'Yes' : 'No';
  if (govt?.usda && typeof govt.usda.usdaGuaranteeFeeFinanced === 'boolean')
    govt.usda.usdaGuaranteeFeeFinanced = govt.usda.usdaGuaranteeFeeFinanced ? 'Yes' : 'No';

  for (const b of snapshot.borrowers || []) {
    if (!Array.isArray(b.phoneNumbers)) b.phoneNumbers = [];
    if (b.phoneNumbers.length === 0 && b.phone && String(b.phone).trim()) {
      b.phoneNumbers = [{ phoneType: 'Mobile', number: String(b.phone).trim(), sortOrder: 0 }];
    }
  }
  return snapshot;
}

const getTabForDirtyKey = (key) => {
  if (/^borrowers\.\d+\.employment/.test(key)) return 'income';
  if (/^borrowers\.\d+\.otherIncome/.test(key)) return 'income';
  if (/^borrowers\.\d+\.creditScores/.test(key)) return 'credit';
  if (key.startsWith('borrowers')) return 'borrower';
  if (key.startsWith('property')) return 'property';
  // Reserves estimate is a Vesta `loan.*` field but is edited on the Assets tab UI.
  if (key === 'loan.monthsOfReservesAvailableEstimate') return 'assets';
  if (key.startsWith('loan')) return 'loan';
  if (key.startsWith('assets')) return 'assets';
  if (key.startsWith('governmentLoanInfo')) return 'government';
  return null;
};

const flattenDirtyFields = (obj, prefix = '') => {
  if (obj === true) return [prefix];
  if (typeof obj !== 'object' || obj === null || Array.isArray(obj) && !obj.length) return [];
  return Object.entries(obj).flatMap(([k, v]) =>
    flattenDirtyFields(v, prefix ? `${prefix}.${k}` : k)
  );
};

const computeDirtyCountByTab = (dirtyFields) => {
  const counts = {};
  for (const key of flattenDirtyFields(dirtyFields)) {
    const tab = getTabForDirtyKey(key);
    if (tab) counts[tab] = (counts[tab] || 0) + 1;
  }
  return counts;
};

const DirtyBadge = ({ tabKey, control }) => {
  const { dirtyFields } = useFormState({ control });
  const count = computeDirtyCountByTab(dirtyFields)[tabKey] || 0;
  if (!count) return null;
  return (
    <Tooltip title={`${count} unsaved change${count !== 1 ? 's' : ''} on this tab`}>
      <Chip label={count} size="small" color="warning" variant="outlined" sx={{ ml: 0.5, height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.5 } }} />
    </Tooltip>
  );
};

const UnsavedChangesAlert = ({ control, onNavigateTab }) => {
  const { dirtyFields } = useFormState({ control });
  const dirtyCountByTab = computeDirtyCountByTab(dirtyFields);
  const totalDirtyCount = Object.values(dirtyCountByTab).reduce((s, c) => s + c, 0);
  if (!totalDirtyCount) return null;
  return (
    <Alert severity="warning" sx={{ mb: 2 }}>
      <Typography variant="body2" component="span">
        <strong>{totalDirtyCount} unsaved change{totalDirtyCount !== 1 ? 's' : ''}</strong>
        {' — '}
        {Object.entries(dirtyCountByTab).map(([tab, count], i) => (
          <span key={tab}>
            {i > 0 && ', '}
            <Link component="button" color="inherit" underline="always" onClick={() => onNavigateTab(tab)}>
              {tab.charAt(0).toUpperCase() + tab.slice(1)} ({count})
            </Link>
          </span>
        ))}
        {'. Remember to save before navigating away.'}
      </Typography>
    </Alert>
  );
};


const LoanDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = authService.getCurrentUser();
  const isBrokerReadOnlyUser = isBrokerReadOnlyRole(currentUser?.role);
  const homeRoute = getHomeRoute(currentUser?.role);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('loan');

  // Post-import state passed from NewLoanModal via navigation
  const [postImportAction] = useState(() => location.state?.postImportAction || null);
  const [pricingFilters] = useState(() => location.state?.pricingFilters || null);
  const [autoRunPricing, setAutoRunPricing] = useState(() => location.state?.postImportAction === 'price');
  const [showAdvisorBanner, setShowAdvisorBanner] = useState(
    () => location.state?.postImportAction === 'advisor-complete',
  );
  const [lockMode, setLockMode] = useState(false);
  const [stalePricingFilters, setStalePricingFilters] = useState(null);
  const postImportNavigationHandledRef = useRef(false);
  const postImportNonQmMortgageAppliedRef = useRef(false);
  const { data: featureFlags = {} } = configurationApi.endpoints.getFeatureFlags.useQuery();
  const { isLockDeskEnabled } = useLockDeskStatus();
  const { data: loanDetailWrapper, isLoading: isLoanLoading, isFetching: isLoanFetching, error: loanError, refetch: refetchLoan } = loansApi.endpoints.getLoanDetail.useQuery(id, { skip: !id });
  const loanDetail = loanDetailWrapper?.loan;
  const [isEditMode, setIsEditMode] = useState(false);
  const [editableMortgageType, setEditableMortgageType] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  /** shouldUnregister: false keeps nested loan fields (e.g. escrow waivers) when other tabs unmount the Loan tab */
  const methods = useForm({ defaultValues: {}, shouldUnregister: false });
  const { control, setValue, reset, getValues } = methods;
  /** Subscribe via useFormState (do not read methods.formState.isDirty directly — it can be stale). */
  const { isDirty } = useFormState({ control });
  const [rateSheetAnchorEl, setRateSheetAnchorEl] = useState(null);
  const watchedNonQmIndicator = useWatch({ control, name: 'loan.nonQualifiedMortgageIndicator' });
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [patchLoan] = loansApi.endpoints.patchLoan.useMutation();
  const [submitLoan] = loansApi.endpoints.submitLoan.useMutation();
  
  // FRED 30-year mortgage rate
  const [fredRate, setFredRate] = useState(null);
  const [fredFailed, setFredFailed] = useState(false);
  useEffect(() => {
    fetchFredMortgageRate().then(r => {
      if (r) setFredRate(r);
      else setFredFailed(true);
    });
  }, []);

  // Tranche 5: Activity
  const [activities, setActivities] = useState([]);
  
  // Tranche 6: Pricing — now handled by PricingTab component
  
  // Tranche 14: AI Chat & Self-Service Actions
  const [isAIChatOpen, setIsAIChatOpen] = useState(false);
  const [showPullCreditModal, setShowPullCreditModal] = useState(false);
  const [showOrderAppraisalModal, setShowOrderAppraisalModal] = useState(false);
  const [showOpenTitleModal, setShowOpenTitleModal] = useState(false);
  const [showOrderVOAModal, setShowOrderVOAModal] = useState(false);
  const [showOrderVOIEModal, setShowOrderVOIEModal] = useState(false);
  const [selectedScenario, setSelectedScenario] = useState(null);
  
  // Tranche 7: Broker ↔ Lender Communication
  const [chatMessages, setChatMessages] = useState([]);
  const [chatThreads, setChatThreads] = useState([]);
  const [activeThread, setActiveThread] = useState('general');
  const [newMessage, setNewMessage] = useState('');
  const [attachedFiles, setAttachedFiles] = useState([]);
  
  // COC (Change of Circumstance) State
  const [cocRequests, setCocRequests] = useState([]);
  const [showCOCForm, setShowCOCForm] = useState(false);
  const [selectedCOCRequest, setSelectedCOCRequest] = useState(null);

  useEffect(() => {
    if (loanError?.status !== 403) return undefined;
    const timer = setTimeout(() => {
      navigate(homeRoute, { replace: true });
    }, 2500);
    return () => clearTimeout(timer);
  }, [loanError, navigate, homeRoute]);

  useEffect(() => {
    postImportNavigationHandledRef.current = false;
    postImportNonQmMortgageAppliedRef.current = false;
  }, [id]);

  /** Doc Order tab: CTC + initial CD signed (keyDates, disclosure timeline, or docOrder checklist). */
  const showDocOrderTab = useMemo(
    () => shouldShowDocOrderTab(loanDetail),
    [loanDetail],
  );

  useEffect(() => {
    if (isLoanLoading) return;
    if (activeTab === 'docorder' && !showDocOrderTab) {
      setActiveTab('loan');
    }
  }, [activeTab, showDocOrderTab, isLoanLoading]);

  /** Non-QM tab should appear immediately when broker toggles Non-QM in edit mode. */
  const showNonQmTab =
    loanDetail?.loan?.nonQualifiedMortgageIndicator === true
    || (isEditMode && (watchedNonQmIndicator === true || watchedNonQmIndicator === 'True'));

  useEffect(() => {
    if (!isEditMode) return;
    const loanIndicator = getValues('loan.nonQualifiedMortgageIndicator');
    const nonQmIndicator = getValues('nonQm.nonQualifiedMortgageIndicator');
    if (loanIndicator !== nonQmIndicator) {
      setValue('nonQm.nonQualifiedMortgageIndicator', loanIndicator, { shouldDirty: false });
    }
  }, [isEditMode, watchedNonQmIndicator, getValues, setValue]);

  useEffect(() => {
    if (isLoanLoading) return;
    if (activeTab === 'nonqm' && !showNonQmTab) {
      setActiveTab('loan');
    }
  }, [activeTab, loanDetail, isLoanLoading, showNonQmTab]);

  /** Keep react-hook-form aligned with the server after load/refetch (view mode only). */
  useEffect(() => {
    if (!loanDetail || isLoanLoading) return;
    if (isEditMode || isDirty) return;
    reset(buildFormSnapshotFromLoanDetail(loanDetail));
  }, [loanDetail, isLoanLoading, isEditMode, isDirty, reset]);

  /** New loan import: when only Non-QM was selected, set Vesta mortgage type to Non-QM on the loan file. */
  useEffect(() => {
    if (!id || !pricingFilters?.applyNonQmMortgageType || postImportNonQmMortgageAppliedRef.current) return;
    if (!loanDetail || isLoanLoading) return;
    if (loanDetailWrapper?.isReadOnly === true || isBrokerReadOnlyUser) return;
    if (loanDetail.loan?.nonQualifiedMortgageIndicator === true) {
      postImportNonQmMortgageAppliedRef.current = true;
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        await patchLoan({
          loanId: id,
          data: {
            loan: {
              mortgageType: VESTA_BASE_MORTGAGE_TYPE_FOR_NON_QM,
              nonQualifiedMortgageIndicator: true,
            },
            nonQm: { nonQualifiedMortgageIndicator: true },
          },
        }).unwrap();
        if (cancelled) return;
        postImportNonQmMortgageAppliedRef.current = true;
        const refreshed = await refetchLoan();
        if (!cancelled && refreshed?.data?.loan && (isEditMode || postImportAction === 'edit')) {
          setEditableMortgageType(NON_QM_MORTGAGE_TYPE_VALUE);
          reset(buildFormSnapshotFromLoanDetail(refreshed.data.loan));
        }
      } catch (err) {
        console.error('[LoanDetailPage] apply Non-QM mortgage type after import:', err);
      }
    })();

    return () => { cancelled = true; };
  }, [
    id,
    loanDetail,
    isLoanLoading,
    pricingFilters,
    loanDetailWrapper?.isReadOnly,
    isBrokerReadOnlyUser,
    isEditMode,
    postImportAction,
    patchLoan,
    refetchLoan,
    reset,
  ]);

  useEffect(() => {
    if (isLoanLoading) return;
    
    if (loanDetail) {
      // Load mock data for features not yet backed by the API
      setActivities(mockActivities.slice(0, 5).sort((a, b) => 
        new Date(b.timestamp) - new Date(a.timestamp)
      ));
      setChatMessages(mockChatMessages.slice(0, 5));
      setChatThreads(mockChatThreads.slice(0, 3));
      // COC requests — no mock data; start empty until backed by API

      // Handle post-import navigation state (from NewLoanModal) — once per loan, not on refetch
      if (!postImportNavigationHandledRef.current && postImportAction) {
        if (postImportAction === 'edit' && loanDetailWrapper?.isReadOnly !== true && !isBrokerReadOnlyUser) {
          setActiveTab('loan');
          setIsEditMode(true);
          setEditableMortgageType(getEffectiveMortgageType(loanDetail?.loan));
          reset(buildFormSnapshotFromLoanDetail(loanDetail));
        } else if (postImportAction === 'price' && !isBrokerReadOnlyUser) {
          setActiveTab('pricing');
        } else if (postImportAction === 'advisor-complete' && !isBrokerReadOnlyUser) {
          setActiveTab('pricing');
          setShowAdvisorBanner(true);
        } else if (postImportAction === 'postlock' && !isBrokerReadOnlyUser) {
          setActiveTab('postlock');
        }
        postImportNavigationHandledRef.current = true;
      }
    }

    setLoading(false);
  }, [id, loanDetail, isLoanLoading, isBrokerReadOnlyUser, postImportAction, reset]);

  const isLoanLocked = loanDetail?.loan?.lockConfirmedDate ? true : false;
  const loanStage = loanDetail?.loan?.stage || '';
  const isReadOnly = loanDetailWrapper?.isReadOnly === true || loanDetail?.isReadOnly === true;
  const isOrigination = !isReadOnly;
  const canEdit = !isReadOnly && !isLoanLocked && !isBrokerReadOnlyUser;
  /** Block self-service, pricing actions, COC, etc. (API-enforced read-only broker role). */
  const blockBrokerReadOnlyActions = isBrokerReadOnlyUser;
  const isGovernmentLoan = isEditMode
    ? GOVERNMENT_MORTGAGE_TYPES.includes(editableMortgageType)
    : !!loanDetail?.governmentLoanInfo;


  const getCurrentEmployer = (b) => {
    if (!b) return '—';
    const current = (b.employment || []).find(j => j.isCurrent);
    return current?.employerName || '—';
  };

  const isFeatureEnabled = (featureKey) => {
    return featureFlags[featureKey] === true;
  };
  const aiQuickActionsEnabled = isFeatureEnabled('loanAIchatQuickActionsEnabled') && isFeatureEnabled('loanAIchatEnabled');
  const aiScenarioEnabled = isFeatureEnabled('loanAIchatScenarioEnabled') && isFeatureEnabled('loanAIchatEnabled');
  const aiFeedbackEnabled = isFeatureEnabled('loanAIchatFeedbackEnabled') && isFeatureEnabled('loanAIchatEnabled');

  const handleEditToggle = () => {
    if (!canEdit) {
      return; // Only allow edit mode during Origination stage and when not locked
    }
    if (isEditMode) {
      setEditableMortgageType(null);
      setValidationErrors({});
      reset({});
    } else {
      setEditableMortgageType(getEffectiveMortgageType(loanDetail?.loan));
      reset(buildFormSnapshotFromLoanDetail(loanDetail));
    }
    setIsEditMode(!isEditMode);
    setSaveSuccess(false);
  };

  const handleMortgageTypeChange = useCallback((value) => {
    const prev = getValues('loan.mortgageType');
    if (value === prev) return;
    if (GOVERNMENT_MORTGAGE_TYPES.includes(value) && !getValues('governmentLoanInfo')) {
      setValue('governmentLoanInfo', { fha: {}, va: {}, usda: {} }, { shouldDirty: true });
    }
    setEditableMortgageType(value);
    setActiveTab(prev => (!GOVERNMENT_MORTGAGE_TYPES.includes(value) && prev === 'government') ? 'loan' : prev);
  }, [getValues, setValue]);

  // Key dates: convert backend array to object for UI components
  const loanDetailWithKeyDates = useMemo(() => {
    if (!loanDetail) return null;
    return {
      ...loanDetail,
      keyDatesObj: keyDatesArrayToObject(loanDetail.keyDates),
      loanStages: loanDetail.loanStages || [],
      currentStageChangedDate: loanDetail.currentStageChangedDate || null,
    };
  }, [loanDetail]);

  // Map validation error keys to the tab they belong to
  const ERROR_KEY_TO_TAB = {
    'borrower.firstName': 'borrower',
    'borrower.lastName': 'borrower',
    'borrower.email': 'borrower',
    'borrower.ssn': 'borrower',
    'borrower.dateOfBirth': 'borrower',
    'borrower.employment': 'income',
    'property.address': 'property',
    'property.city': 'property',
    'property.state': 'property',
    'property.zip': 'property',
    'property.propertyType': 'property',
    'property.occupancy': 'property',
    'property.purchasePrice': 'property',
    'loan.loanAmount': 'loan',
    'loan.noteRate': 'loan',
    'loan.programName': 'loan',
    'loan.mortgageType': 'loan',
    'loan.earnestMoneyDepositAmount': 'loan',
    'loan.sellerPaidClosingCostsAmount': 'loan',
  };

  const getTabsWithErrors = () => {
    const tabs = new Set();
    for (const key of Object.keys(validationErrors)) {
      let tab = ERROR_KEY_TO_TAB[key];
      if (!tab && /^borrowers\.\d+\.phoneNumbers/.test(key)) tab = 'borrower';
      if (tab) tabs.add(tab);
    }
    return tabs;
  };

  const tabsWithErrors = getTabsWithErrors();

  const tabErrorDot = (tabKey) => {
    if (!tabsWithErrors.has(tabKey)) return null;
    return (
      <Tooltip title="This tab has validation errors">
        <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main', display: 'inline-block', ml: 0.5, verticalAlign: 'middle' }} />
      </Tooltip>
    );
  };


  const validateLoan = () => {
    const snapshot = getValues();
    if (!snapshot || Object.keys(snapshot).length === 0) return {};
    const errors = {};
    const b = snapshot.borrowers?.[0];
    const p = snapshot.property;
    const l = snapshot.loan;
    
    // Required borrower fields (URLA Section 1a)
    if (!b?.firstName?.trim()) errors['borrower.firstName'] = 'First name is required';
    if (!b?.lastName?.trim()) errors['borrower.lastName'] = 'Last name is required';
    if (!b?.email?.trim()) errors['borrower.email'] = 'Email is required';
    if (!b?.ssn?.trim()) errors['borrower.ssn'] = 'SSN is required';
    if (!b?.dateOfBirth) errors['borrower.dateOfBirth'] = 'Date of birth is required';
    // Note: Credit score is read-only (comes from credit pulls in the LOS) — no validation needed.
    
    
    // Required property fields
    if (!p?.address?.street?.trim()) errors['property.address'] = 'Address is required';
    if (!p?.address?.city?.trim()) errors['property.city'] = 'City is required';
    if (!p?.address?.state?.trim()) errors['property.state'] = 'State is required';
    if (!p?.address?.zip?.trim()) errors['property.zip'] = 'ZIP code is required';
    if (!p?.propertyType?.trim()) errors['property.propertyType'] = 'Property type is required';
    if (!p?.occupancy?.trim()) errors['property.occupancy'] = 'Occupancy is required';
    const isPurchase = l?.loanPurpose === 'Purchase';
    if (isPurchase && (!p?.purchasePrice || p.purchasePrice <= 0)) {
      errors['property.purchasePrice'] = 'Purchase price is required';
    }
    
    // Required loan fields
    if (!l?.loanAmount || l.loanAmount <= 0) {
      errors['loan.loanAmount'] = 'Loan amount is required';
    }
    // noteRate and programName are set via pricing assignment — not validated here
    if (!l?.mortgageType?.trim()) errors['loan.mortgageType'] = 'Mortgage type is required';

    // EMD & seller-paid closing costs are optional but must be non-negative when provided (ENG-248 / pro-api#127).
    const nonNegativeAmount = (val, key, label) => {
      if (val === '' || val === null || val === undefined) return;
      const num = Number(val);
      if (Number.isNaN(num) || num < 0) errors[key] = `${label} cannot be negative`;
    };
    nonNegativeAmount(l?.earnestMoneyDepositAmount, 'loan.earnestMoneyDepositAmount', 'Earnest money deposit');
    nonNegativeAmount(l?.sellerPaidClosingCostsAmount, 'loan.sellerPaidClosingCostsAmount', 'Seller-paid closing costs');

    (snapshot.borrowers || []).forEach((b, i) => {
      const list = b.phoneNumbers || [];
      if (list.length > 10) {
        errors[`borrowers.${i}.phoneNumbers`] = 'At most 10 phone numbers per borrower';
      }
      list.forEach((row, j) => {
        const num = (row.number || '').trim();
        if (!num) return;
        if (!row.phoneType || !BORROWER_PHONE_TYPES.includes(row.phoneType)) {
          errors[`borrowers.${i}.phoneNumbers.${j}.phoneType`] = 'Select a valid phone type';
        }
        if (!/\d/.test(num)) {
          errors[`borrowers.${i}.phoneNumbers.${j}.number`] = 'Phone number must contain at least one digit';
        }
      });
    });

    return errors;
  };

  const handleSave = async () => {
    const errors = validateLoan();

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setIsSaving(true);
    try {
      // Strip government sub-sections that don't match the current mortgage type
      // so stale data from a previous type isn't sent to the backend.
      const payload = { ...getValues() };
      // Convert 'Yes'/'No' back to boolean for govt fields
      const govt = payload.governmentLoanInfo;
      if (govt?.fha) {
        if (govt.fha.upfrontMipFinanced === 'Yes') govt.fha.upfrontMipFinanced = true;
        else if (govt.fha.upfrontMipFinanced === 'No') govt.fha.upfrontMipFinanced = false;
      }
      if (govt?.va) {
        if (govt.va.vaFundingFeeFinanced === 'Yes') govt.va.vaFundingFeeFinanced = true;
        else if (govt.va.vaFundingFeeFinanced === 'No') govt.va.vaFundingFeeFinanced = false;
      }
      if (govt?.usda) {
        if (govt.usda.usdaGuaranteeFeeFinanced === 'Yes') govt.usda.usdaGuaranteeFeeFinanced = true;
        else if (govt.usda.usdaGuaranteeFeeFinanced === 'No') govt.usda.usdaGuaranteeFeeFinanced = false;
      }
      const mt = payload.loan?.mortgageType;
      if (payload.governmentLoanInfo) {
        if (!GOVERNMENT_MORTGAGE_TYPES.includes(mt)) {
          payload.governmentLoanInfo = null;
        } else {
          payload.governmentLoanInfo = {
            fha: mt === 'FHA' ? payload.governmentLoanInfo.fha : null,
            va: mt === 'VA' ? payload.governmentLoanInfo.va : null,
            usda: mt === 'USDA' ? payload.governmentLoanInfo.usda : null,
          };
        }
      }
      // Ensure escrow waiver booleans from the form are on loan (PATCH must persist these on the API side)
      const loanFromForm = getValues('loan');
      if (payload.loan && loanFromForm) {
        const escrowKeys = ['escrowPropertyTaxesWaiverIndicator', 'escrowPropertyInsuranceExFloodWaiverIndicator'];
        for (const key of escrowKeys) {
          if (loanFromForm[key] !== undefined) {
            payload.loan[key] = loanFromForm[key];
          }
        }
        // Edited on Assets tab — ensure PATCH diff sees broker estimate (Vesta writable field).
        if (Object.prototype.hasOwnProperty.call(loanFromForm, 'monthsOfReservesAvailableEstimate')) {
          payload.loan.monthsOfReservesAvailableEstimate = loanFromForm.monthsOfReservesAvailableEstimate;
        }
      }
      for (const borrower of payload.borrowers || []) {
        if (!borrower.phoneNumbers || !Array.isArray(borrower.phoneNumbers)) continue;
        borrower.phoneNumbers = borrower.phoneNumbers
          .map((row, idx) => ({
            phoneType: row.phoneType,
            number: (row.number || '').trim(),
            sortOrder: idx,
          }))
          .filter((row) => row.number.length > 0);
      }
      if (payload.loan && payload.loan.nonQualifiedMortgageIndicator !== undefined && payload.loan.nonQualifiedMortgageIndicator !== null) {
        const v = payload.loan.nonQualifiedMortgageIndicator;
        if (v === 'True') payload.loan.nonQualifiedMortgageIndicator = true;
        else if (v === 'False') payload.loan.nonQualifiedMortgageIndicator = false;
      }
      if (!payload.nonQm || typeof payload.nonQm !== 'object') payload.nonQm = {};
      if (payload.nonQm.nonQualifiedMortgageIndicator !== undefined && payload.nonQm.nonQualifiedMortgageIndicator !== null) {
        const v = payload.nonQm.nonQualifiedMortgageIndicator;
        if (v === 'True') payload.nonQm.nonQualifiedMortgageIndicator = true;
        else if (v === 'False') payload.nonQm.nonQualifiedMortgageIndicator = false;
      }
      // "Non-QM" is a UI-only Mortgage Type → Vesta stores Conventional + indicator=true.
      const wasNonQmUiMortgageType = payload.loan?.mortgageType === NON_QM_MORTGAGE_TYPE_VALUE;
      if (wasNonQmUiMortgageType) {
        payload.loan.mortgageType = VESTA_BASE_MORTGAGE_TYPE_FOR_NON_QM;
      }

      // Always send both indicator locations for API compatibility.
      const unifiedIndicator = payload.loan?.nonQualifiedMortgageIndicator ?? payload.nonQm?.nonQualifiedMortgageIndicator ?? null;
      if (payload.loan) payload.loan.nonQualifiedMortgageIndicator = unifiedIndicator;
      payload.nonQm.nonQualifiedMortgageIndicator = unifiedIndicator;
      const isNonQmActive = isNonQmIndicatorTrue(unifiedIndicator) || wasNonQmUiMortgageType;

      // Coerce the Non-QM dscr indicator (form select can return strings).
      if (payload.nonQm.underwrittenUsingDSCRIndicator === 'True') payload.nonQm.underwrittenUsingDSCRIndicator = true;
      else if (payload.nonQm.underwrittenUsingDSCRIndicator === 'False') payload.nonQm.underwrittenUsingDSCRIndicator = false;
      // Numeric coercion for DSCR estimate (text input → decimal).
      if (typeof payload.nonQm.subjectPropertyDSCREstimate === 'string' && payload.nonQm.subjectPropertyDSCREstimate.trim() !== '') {
        const parsed = Number(payload.nonQm.subjectPropertyDSCREstimate);
        payload.nonQm.subjectPropertyDSCREstimate = Number.isFinite(parsed) ? parsed : null;
      }
      // When Non-QM is off, clear the four derived fields so Vesta nulls them on PATCH.
      if (!isNonQmActive) {
        payload.nonQm.alternativeIncomeVerificationType = null;
        payload.nonQm.alternativeIncomeDocumentationNumberOfMonths = null;
        payload.nonQm.underwrittenUsingDSCRIndicator = null;
        payload.nonQm.subjectPropertyDSCREstimate = null;
      }
      // Server-derived for pricing display — do not PATCH from the form
      if (payload.loan) {
        delete payload.loan.cashOutAmount;
        delete payload.loan.CashOutAmount;
      }
      await patchLoan({ loanId: id, data: payload }).unwrap();
      const refetchResult = await refetchLoan();
      setIsEditMode(false);
      setEditableMortgageType(null);
      setValidationErrors({});
      reset(buildFormSnapshotFromLoanDetail(refetchResult.data?.loan ?? refetchResult.data));
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      const msg = getAxiosErrorMessage(err, 'Failed to save loan changes.');
      setValidationErrors({ _save: msg });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmitToUFF = async () => {
    if (isReadOnly || blockBrokerReadOnlyActions) return;
    if (isDirty) {
      setValidationErrors({ _save: 'You have unsaved changes. Please save before submitting to UFF.' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    const confirmed = await confirm({
      title: 'Submit to UFF',
      message: 'Submit this loan to UFF? The loan will move to the Setup stage and editing will be restricted.',
      confirmText: 'Submit',
      confirmClass: 'btn-primary',
    });
    if (!confirmed) return;

    setIsSubmitting(true);
    try {
      await submitLoan(id).unwrap();
      const { data: refreshed } = await refetchLoan();
      if (refreshed?.loan) {
        reset(buildFormSnapshotFromLoanDetail(refreshed.loan));
      }
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 5000);
    } catch (err) {
      const msg = getAxiosErrorMessage(err, 'Failed to submit loan to UFF.');
      setValidationErrors({ _save: msg });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSubmitting(false);
    }
  };


  const isLockExpiringSoon = (lockExpiration) => {
    const expirationDate = new Date(lockExpiration);
    const today = new Date();
    const daysUntilExpiration = Math.ceil((expirationDate - today) / (1000 * 60 * 60 * 24));
    return daysUntilExpiration <= 7;
  };


  // Tranche 14: Self-Service Action Handlers
  const handlePullCredit = (data) => {
    const newActivity = {
      id: Date.now(),
      type: 'credit_pull',
      description: `Credit report pulled (${data.creditType} pull, ${data.borrowerScope})`,
      user: 'Michael Chen',
      timestamp: data.timestamp,
      details: data
    };
    setActivities([newActivity, ...activities]);
    setShowPullCreditModal(false);
    showToast('Credit report ordered successfully! Results will be available within 2-3 minutes.');
  };

  const handleOrderAppraisal = (data) => {
    const newActivity = {
      id: Date.now(),
      type: 'appraisal_order',
      description: `Appraisal ordered (${data.appraisalType})`,
      user: 'Michael Chen',
      timestamp: data.timestamp,
      details: data
    };
    setActivities([newActivity, ...activities]);
    setShowOrderAppraisalModal(false);
    showToast('Appraisal ordered successfully! Appraiser will be assigned within 24 hours.');
  };

  const handleOpenTitle = (data) => {
    const newActivity = {
      id: Date.now(),
      type: 'title_order',
      description: `Title order opened (${data.transactionType})`,
      user: 'Michael Chen',
      timestamp: data.timestamp,
      details: data
    };
    setActivities([newActivity, ...activities]);
    setShowOpenTitleModal(false);
    showToast('Title order opened successfully! Title company will contact you within 1 business day.');
  };

  const handleOrderVOA = (data) => {
    const newActivity = {
      id: Date.now(),
      type: 'voa_order',
      description: `Digital asset verification (VOA) ordered`,
      user: 'Michael Chen',
      timestamp: data.timestamp,
      details: data
    };
    setActivities([newActivity, ...activities]);
    setShowOrderVOAModal(false);
    showToast('VOA ordered successfully! Borrower will receive secure link to connect accounts.');
  };

  const handleOrderVOIE = (data) => {
    const newActivity = {
      id: Date.now(),
      type: 'voie_order',
      description: `Digital income/employment verification (VOIE) ordered`,
      user: 'Michael Chen',
      timestamp: data.timestamp,
      details: data
    };
    setActivities([newActivity, ...activities]);
    setShowOrderVOIEModal(false);
    showToast('VOIE ordered successfully! Borrower will receive secure link to connect payroll.');
  };

  const handleCOCSubmit = (cocRequest) => {
    setCocRequests([cocRequest, ...cocRequests]);
    setShowCOCForm(false);
    
    const newActivity = {
      id: Date.now(),
      type: 'coc_submitted',
      description: `Change of Circumstance request submitted: ${cocRequest.categoryName}`,
      user: cocRequest.submittedBy,
      timestamp: cocRequest.submittedDate,
      details: cocRequest
    };
    setActivities([newActivity, ...activities]);
    
    showToast('COC request submitted successfully! The lender will review your request and respond within 2 business days.');
  };

  const { showToast, ToastContainer } = useToast();
  const { confirm, ConfirmModalContainer } = useConfirmModal();

  const [runPricing] = pricingApi.endpoints.runPricing.useMutation();
  const [runLoanPricing] = pricingApi.endpoints.runLoanPricing.useMutation();
  const [assignProduct] = loansApi.endpoints.assignProduct.useMutation();
  const [fetchCountyLimit] = useLazyGetCountyLoanLimitQuery();
  const [isTilePricingRefreshing, setIsTilePricingRefreshing] = useState(false);

  const handleTilePricingRefresh = useCallback(async () => {
    if (isDirty) {
      showToast('Save your loan changes before refreshing pricing.', 'warning');
      return;
    }
    const ld = loanDetail;
    const pricingRequest = loanDetailWrapper?.pricingRequest;
    if (!pricingRequest) {
      showToast('Pricing request is unavailable for this loan.', 'danger');
      return;
    }
    const loan = ld.loan || {};
    const productCode = loan.productCode;
    const noteRate = loan.noteRate;
    const lockTerm = loan.rateLockPeriod || 30;
    if (!productCode || noteRate == null) {
      showToast('Assign a product and rate before refreshing pricing.', 'warning');
      return;
    }

    const family = resolveMortgageTypeToPricingFamily(loan.mortgageType);
    const selectedFamilies = family ? [family] : [];
    const termYears = loan.loanTermMonths ? Math.round(loan.loanTermMonths / 12) : null;
    const selectedTerms = termYears ? [termYears] : [];
    const rawAmort = (loan.amortizationType || '').toLowerCase();
    const selectedAmortTypes = rawAmort.includes('arm') ? ['ARM'] : ['Fixed'];

    setIsTilePricingRefreshing(true);
    try {
      const { fipsCode, numberOfUnits } = getLoanPropertyInfo(ld);
      const baseLoanAmount = loan.loanAmount || 0;
      const limitFamilies = selectedFamilies.filter((f) => appliesCountyLoanLimit(f));
      let families = selectedFamilies.length > 0 ? [...selectedFamilies] : undefined;
      let subFamilies = [];

      if (limitFamilies.length > 0 && fipsCode && baseLoanAmount > 0) {
        const { countyLimitsCache, countyLimitUnavailable } = await fetchCountyLimitsForPricing(
          fetchCountyLimit,
          fipsCode,
          limitFamilies,
        );

        const {
          perFamilyResults,
          anyExceedsStandard,
          allExceedCounty,
        } = evaluateCountyLoanLimits(
          baseLoanAmount,
          numberOfUnits,
          limitFamilies,
          countyLimitsCache,
          countyLimitUnavailable,
          fipsCode,
        );

        let hasGovernmentExceed = false;
        let hasConventionalExceed = false;

        for (const result of perFamilyResults) {
          if (result.exceedsStandardLimit) {
            if (['FHA', 'VA', 'USDA'].includes(result.family)) hasGovernmentExceed = true;
            else hasConventionalExceed = true;
          }
        }

        if (allExceedCounty && limitFamilies.length > 0 && perFamilyResults.some((r) => r.exceedsCountyLimit)) {
          showToast('Loan amount exceeds county limit for this program — cannot refresh pricing.', 'danger');
          return;
        }

        if (anyExceedsStandard) {
          const subFamilySet = new Set();
          if (hasGovernmentExceed) subFamilySet.add('High Balance');
          if (hasConventionalExceed) {
            subFamilySet.add('High Balance');
            subFamilySet.add('Super Conforming');
          }
          subFamilies = [...subFamilySet];
        }
      }

      const request = JSON.parse(JSON.stringify(pricingRequest));
      request.loan = request.loan || {};
      request.options = request.options || {};
      request.loan.submissionDate = new Date().toISOString();
      if (request.loan.compensationType == null) request.loan.compensationType = 'LPC';
      request.options.lockTerm = lockTerm;
      request.options.productFamilies = families || [];
      request.options.productSubFamilies = subFamilies.length > 0 ? subFamilies : [];
      request.options.terms = selectedTerms.length > 0 ? selectedTerms : [];
      request.options.productTypes = selectedAmortTypes.length > 0 ? selectedAmortTypes : [];

      const fico = applyRepresentativeFicoToPricingLoan(loanDetail, request.loan);
      if (fico == null) {
        showToast('A valid credit score (300–850) is required to refresh pricing.', 'warning');
        return;
      }

      const response = await runPricingWithEngine({
        featureFlags,
        pricingRequest: request,
        loanId: id,
        runPricing,
        runLoanPricing,
      });
      const products = response.products || [];
      const target = products.find((p) => p.productShortName === productCode);
      if (!target) {
        showToast('Assigned product was not returned by the pricing engine. Open Pricing to review.', 'danger');
        return;
      }
      const matchedRate = (target.prices || []).find((rp) => Math.abs(Number(rp.rate) - Number(noteRate)) < 0.0005);
      if (!matchedRate) {
        showToast('The current rate is no longer available. Open Pricing to select a rate.', 'danger');
        return;
      }

      const assignPayload = {
        loanId: id,
        pricingRequest: request,
        productId: target.productId,
        rate: matchedRate.rate,
        price: matchedRate.adjustedPrice,
        lockTerm,
        ...(matchedRate.quoteId ? { quoteId: matchedRate.quoteId } : {}),
      };
      const comp = request.loan?.compensationType || 'LPC';
      if (comp === 'BPC' && request.loan?.bpcValue != null) {
        assignPayload.bpcCalcMethod = request.loan.bpcCalcMethod;
        assignPayload.bpcValue = Number(request.loan.bpcValue);
      }

      await assignProduct(assignPayload).unwrap();

      refetchLoan();
      showToast('Price updated from current rate sheet.', 'success');
    } catch (err) {
      console.error('[LoanDetailPage] tile pricing refresh:', err);
      showToast(getAxiosErrorMessage(err, 'Failed to refresh pricing.'), 'danger');
    } finally {
      setIsTilePricingRefreshing(false);
    }
  }, [
    id,
    loanDetail,
    loanDetailWrapper,
    isDirty,
    showToast,
    fetchCountyLimit,
    featureFlags,
    runPricing,
    runLoanPricing,
    assignProduct,
    refetchLoan,
  ]);

  if (loading || isLoanLoading) {
    return <LoadingSkeleton variant="detail" />;
  }

  if (loanError) {
    const isAccessDenied = loanError?.status === 403;
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <WarningAmberIcon sx={{ fontSize: 48, color: 'warning.main', mb: 2 }} />
        <Typography variant="h6">{isAccessDenied ? 'Access denied' : 'Error loading loan'}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {isAccessDenied
            ? 'You do not have permission to access this loan. Redirecting you to your dashboard.'
            : getAxiosErrorMessage(loanError, 'An error occurred while loading the loan details.')}
        </Typography>
        <Button variant="contained" startIcon={<ArrowBackIcon />} onClick={() => navigate(isAccessDenied ? homeRoute : -1, isAccessDenied ? { replace: true } : undefined)}>
          {isAccessDenied ? 'Go to Dashboard' : 'Go Back'}
        </Button>
      </Box>
    );
  }

  if (!loanDetail) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6">Loan not found</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The loan you're looking for doesn't exist or has been removed.
        </Typography>
        <Button variant="contained" startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>Go Back</Button>
      </Box>
    );
  }

  return (
    <FormProvider {...methods}>
    <div>
      <Box>
        {ToastContainer}
        {ConfirmModalContainer}

        {/*
         * Loan context lives in the global AppBar via Portal so it follows the user on scroll.
         * The slot is provided by AppLayout (id=PAGE_HEADER_SLOT_ID); when the slot doesn't exist
         * yet (first paint), Portal silently no-ops and we re-render once the layout mounts.
         */}
        <Portal container={() => document.getElementById(PAGE_HEADER_SLOT_ID)}>
          <Stack direction="row" alignItems="center" sx={{ width: '100%', gap: 2, minWidth: 0 }}>
            <Tooltip title="Back to Pipeline">
              <IconButton size="small" onClick={() => navigate(-1)} aria-label="back to pipeline">
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                <Typography variant="h6" fontWeight={700} noWrap>
                  {loanDetail?.loan?.loanNumber || loanDetail?.loan?.loanId}
                </Typography>
                <Tooltip title={isEditMode ? 'Save or cancel edits before refreshing' : 'Refresh loan data'}>
                  <span>
                    <IconButton size="small" onClick={refetchLoan} disabled={isLoanFetching || isEditMode}>
                      {isLoanFetching ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>
                <StatusBadge status={loanDetail?.loan?.stage} map={LOAN_STATUS_MAP}>
                  {loanDetail?.loan?.stage}
                </StatusBadge>
                {isEditMode && (
                  <Chip icon={<EditIcon />} label="Editing" size="small" color="warning" variant="outlined" />
                )}
              </Stack>
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                sx={{ display: 'block', lineHeight: 1.2 }}
              >
                {loanDetail?.borrowers?.[0] ? `${loanDetail.borrowers[0].firstName || ''} ${loanDetail.borrowers[0].lastName || ''}`.trim() : ''}
                {loanDetail?.borrowers?.[0] && loanDetail?.property?.address ? ' • ' : ''}
                {loanDetail?.property?.address ? `${loanDetail.property.address.street || ''}, ${loanDetail.property.address.city || ''}, ${loanDetail.property.address.state || ''}`.replace(/^, |, $/g, '') : ''}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexShrink={0}>
              {isEditMode ? (
                <>
                  <Button size="small" color="inherit" startIcon={<CloseIcon />} onClick={handleEditToggle}>
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </>
              ) : (
                <>
                  {canEdit && (
                    <Button size="small" variant="contained" startIcon={<EditIcon />} onClick={handleEditToggle}>
                      Edit Loan
                    </Button>
                  )}
                  {!isReadOnly && !isEditMode && !blockBrokerReadOnlyActions && (
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                      onClick={handleSubmitToUFF}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit to UFF'}
                    </Button>
                  )}
                </>
              )}
            </Stack>
          </Stack>
        </Portal>
      </Box>
      <div>
        {/* Locked Loan Alert */}
        {isLoanLocked && !isEditMode && (
          <Alert severity="warning" icon={<LockIcon />} sx={{ mb: 2 }}>
            <Typography variant="subtitle2">Loan is Locked</Typography>
            <Typography variant="body2" color="text.secondary">
              This loan is currently locked with a rate of {loanDetail?.loan?.noteRate != null ? Number(loanDetail.loan.noteRate).toFixed(3) : '—'}% until {loanDetail?.loan?.lockExpirationDate ? new Date(loanDetail.loan.lockExpirationDate).toLocaleDateString() : '—'}.
              Editing is disabled while the loan is locked to preserve rate lock integrity.
            </Typography>
          </Alert>
        )}

        {/* Non-Origination Info */}
        {isReadOnly && !isLoanLocked && !isEditMode && !blockBrokerReadOnlyActions && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="subtitle2">Loan in {loanStage || 'Post-Origination'} Stage</Typography>
            <Typography variant="body2" color="text.secondary">
              This loan is read-only. You can still run pricing/lock, submit COC requests, and perform post-lock actions.
            </Typography>
          </Alert>
        )}
        {isReadOnly && !isLoanLocked && !isEditMode && blockBrokerReadOnlyActions && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="subtitle2">Loan in {loanStage || 'Post-Origination'} Stage</Typography>
            <Typography variant="body2" color="text.secondary">
              This loan is read-only in UFF. You can view details; your account cannot change loans or pricing.
            </Typography>
          </Alert>
        )}

        {/* Success Alert */}
        {saveSuccess && (
          <Alert severity="success" onClose={() => setSaveSuccess(false)} sx={{ mb: 2 }}>
            <Typography variant="subtitle2">Changes Saved Successfully</Typography>
            <Typography variant="body2" color="text.secondary">Your loan updates have been saved.</Typography>
          </Alert>
        )}

        {/* Validation Errors Alert */}
        {Object.keys(validationErrors).length > 0 && (
          <Alert severity="error" onClose={() => setValidationErrors({})} sx={{ mb: 2 }}>
            <Typography variant="subtitle2">Validation Errors</Typography>
            <Typography variant="body2" color="text.secondary" component="div">
              Please fix the following errors before saving:
              <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
                {Object.entries(validationErrors).map(([key, error]) => {
                  const tab = ERROR_KEY_TO_TAB[key];
                  const TAB_LABELS = { borrower: 'Borrower', income: 'Income', property: 'Property', loan: 'Loan', credit: 'Credit / Liabilities', assets: 'Assets' };
                  return (
                    <li key={key}>
                      {tab ? (
                        <Link
                          component="button"
                          color="error"
                          underline="always"
                          onClick={() => setActiveTab(tab)}
                          sx={{ fontWeight: 700, verticalAlign: 'baseline' }}
                        >
                          {TAB_LABELS[tab] || tab}
                        </Link>
                      ) : null}
                      {tab ? ': ' : ''}{error}
                    </li>
                  );
                })}
              </ul>
            </Typography>
          </Alert>
        )}

        {/* Edit Mode Info */}
        {isEditMode && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="subtitle2">Edit Mode Active</Typography>
            <Typography variant="body2" color="text.secondary">
              Fields marked with <Typography component="span" color="error">*</Typography> are required.
              Make your changes and click "Save Changes" when done.
            </Typography>
          </Alert>
        )}

        {isEditMode && <UnsavedChangesAlert control={control} onNavigateTab={setActiveTab} />}

        {/*
         * Pricing-outdated banner — promoted out of the Program/Lock tile so the warning is visible
         * regardless of tile width and so the tile stays the same height as its peers.
         */}
        {!isLoanLocked
          && loanDetailWrapper?.isAssignedPricingCurrent === false
          && (loanDetail?.loan?.quoteId != null || loanDetail?.loan?.quotedAtDateTime != null) && (
          <Alert severity="warning" sx={{ mb: 2 }}
            action={(
              <Button
                size="small"
                color="warning"
                variant="contained"
                startIcon={isTilePricingRefreshing ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon sx={{ fontSize: 16 }} />}
                disabled={isTilePricingRefreshing || !loanDetailWrapper?.pricingRequest || isDirty}
                onClick={handleTilePricingRefresh}
              >
                Refresh Pricing
              </Button>
            )}
          >
            <Typography variant="subtitle2">Pricing outdated</Typography>
            <Typography variant="body2" color="text.secondary">
              The current rate sheet has changed since this loan was priced. Refresh to update pricing for the current program, rate, and lock term.
            </Typography>
          </Alert>
        )}

        {/* Key Metrics Cards — Order: Loan Amount, Interest Rate, Program/Lock, Stage */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {/* 1. Loan Amount Tile */}
          <Grid size={{ xs: 6, lg: 3 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="overline" color="text.secondary">Loan Amount</Typography>
                <Typography variant="h5" fontWeight={700} className="tabular-nums">{loanDetail?.loan?.loanAmount != null ? fmtDollar(loanDetail.loan.loanAmount) : '—'}</Typography>
                {(() => {
                  const loan = loanDetail?.loan || {};
                  const prop = loanDetail?.property || {};
                  const amount = loan.loanAmount || 0;
                  const propertyValue = prop.purchasePrice || prop.appraisedValue || prop.estimatedValue || 0;
                  const ltvRaw = (amount && propertyValue) ? ((amount / propertyValue) * 100) : null;
                  const ltv = loan.ltv || (ltvRaw != null ? Math.round(ltvRaw * 100) / 100 : null);
                  const downPmt = loan.downPaymentAmount || (propertyValue ? propertyValue - amount : null);
                  const downPctRaw = loan.downPaymentPercent || ((propertyValue && downPmt) ? ((downPmt / propertyValue) * 100) : null);
                  const downPct = downPctRaw != null ? Math.round(downPctRaw * 100) / 100 : null;

                  return (
                    <Box sx={{ fontSize: '0.75rem' }}>
                      {ltv != null && (
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="caption" color="text.secondary">LTV</Typography>
                          <Typography variant="caption" fontWeight={700} color={ltv > 80 ? 'warning.main' : 'success.main'}>{fmtPct(ltv)}</Typography>
                        </Stack>
                      )}
                      {downPmt != null && downPmt > 0 && (
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="caption" color="text.secondary">Down Payment</Typography>
                          <Typography variant="caption" fontWeight={700}>{fmtDollar(Math.round(downPmt))} {downPct ? `(${fmtPct(downPct)})` : ''}</Typography>
                        </Stack>
                      )}
                      {propertyValue > 0 && (
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="caption" color="text.secondary">Property Value</Typography>
                          <Typography variant="caption" fontWeight={700}>{fmtDollar(propertyValue)}</Typography>
                        </Stack>
                      )}
                      {loan.brokerCompensation != null && loan.brokerCompensation > 0 && (
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="caption" color="text.secondary">Comp</Typography>
                          <Typography variant="caption" fontWeight={700}>{fmtPricingPct(loan.brokerCompensation)} <Typography component="span" variant="caption" color="text.secondary">({fmtDollar(Math.round(amount * loan.brokerCompensation / 100))})</Typography></Typography>
                        </Stack>
                      )}
                    </Box>
                  );
                })()}
              </CardContent>
            </Card>
          </Grid>

          {/* 2. Interest Rate Tile */}
          <Grid size={{ xs: 6, lg: 3 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="overline" color="text.secondary">Interest Rate</Typography>
                <Typography variant="h5" fontWeight={700} className="tabular-nums">{loanDetail?.loan?.noteRate != null ? `${Number(loanDetail.loan.noteRate).toFixed(3)}%` : '—'}</Typography>
                {(() => {
                  const loan = loanDetail?.loan || {};
                  const rate = loan.noteRate;
                  const amount = loan.loanAmount;
                  const termMonths = loan.loanTermMonths || 360;
                  const pi = loan.monthlyPrincipalAndInterest;
                  const monthlyRate = rate ? rate / 100 / 12 : null;
                  const calcPI = (monthlyRate && amount && termMonths)
                    ? (amount * monthlyRate * Math.pow(1 + monthlyRate, termMonths)) / (Math.pow(1 + monthlyRate, termMonths) - 1)
                    : null;
                  const monthlyPI = pi || calcPI;
                  const displayTotal = getDisplayTotalMonthlyPayment(loan);

                  return (
                    <Box sx={{ fontSize: '0.75rem' }}>
                      {loan.rateLockStatus && <Typography variant="caption" color="text.secondary">{loan.rateLockStatus}</Typography>}
                      {monthlyPI != null && (
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="caption" color="text.secondary">P&I</Typography>
                          <Typography variant="caption" fontWeight={700}>{fmtDollar(Math.round(monthlyPI))}/mo</Typography>
                        </Stack>
                      )}
                      {displayTotal != null && monthlyPI != null && Math.round(displayTotal) !== Math.round(monthlyPI) && (
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="caption" color="text.secondary">Total</Typography>
                          <Typography variant="caption" fontWeight={700}>{fmtDollar(Math.round(displayTotal))}/mo</Typography>
                        </Stack>
                      )}
                    </Box>
                  );
                })()}
              </CardContent>
            </Card>
          </Grid>

          {/* 3. Program / Lock Tile */}
          <Grid size={{ xs: 6, lg: 3 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                {(() => {
                  const loan = loanDetail?.loan || {};
                  const hasProduct = !!(loan.programName || loan.productCode);
                  const displayPrice = loan.loanOfficerPrice;
                  const displayDate = loan.quotedAtDateTime;
                  const hasAssignedPricingSnapshot = !!(loan.quoteId || loan.quotedAtDateTime);
                  const isPricingStale = loanDetailWrapper?.isAssignedPricingCurrent === false && hasAssignedPricingSnapshot;
                  const loanAmount = loan.loanAmount || 0;
                  const priceDollars = displayPrice != null && loanAmount
                    ? ((displayPrice - 100) / 100 * loanAmount)
                    : null;
                  const priceColor = displayPrice != null
                    ? (displayPrice >= 100 ? 'success.main' : 'error.main')
                    : 'text.primary';
                  const priceLabel = displayPrice != null
                    ? (displayPrice >= 100 ? 'Lender Credit' : 'Discount Points')
                    : '';
                  const compMode = (loan.compensationType || loanDetailWrapper?.pricingRequest?.loan?.compensationType || 'LPC').toUpperCase() === 'BPC' ? 'BPC' : 'LPC';
                  const allInCaption = compMode === 'BPC'
                    ? 'All-in loan officer price — BPC (borrower-paid compensation per your pricing selection).'
                    : 'All-in loan officer price — LPC (lender-paid compensation included in this price).';
                  const fmtRateSheetDisplay = (val) => {
                    if (val == null || val === '') return '—';
                    const d = new Date(val);
                    return Number.isNaN(d.getTime()) ? String(val) : d.toLocaleString();
                  };
                  const assignedSheet =
                    loanDetailWrapper?.assignedRateSheetIdentifier
                    ?? loanDetail?.assignedRateSheetIdentifier
                    ?? loan?.effectiveRateSheetIdentifier;
                  /**
                   * Compact info trigger — replaces the three-line meta block (all-in caption +
                   * assigned/current rate sheet) with a small ⓘ button that opens a Popover.
                   * Keeps the tile height in line with its peers without losing any information.
                   */
                  const pricingDetailsPopover = (
                    <>
                      <Tooltip title="Pricing details">
                        <IconButton
                          size="small"
                          onClick={(e) => setRateSheetAnchorEl(e.currentTarget)}
                          sx={{ ml: 0.25, p: 0.25 }}
                        >
                          <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                      <Popover
                        open={Boolean(rateSheetAnchorEl)}
                        anchorEl={rateSheetAnchorEl}
                        onClose={() => setRateSheetAnchorEl(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        slotProps={{ paper: { sx: { p: 1.5, maxWidth: 320 } } }}
                      >
                        <Stack spacing={0.5}>
                          <Typography variant="caption" color="text.secondary">{allInCaption}</Typography>
                          {displayDate && (
                            <Typography variant="caption" color="text.secondary">
                              Priced: {new Date(displayDate).toLocaleString()}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            Assigned rate sheet: {fmtRateSheetDisplay(assignedSheet)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Current rate sheet: {fmtRateSheetDisplay(loanDetailWrapper?.currentEngineRateSheetIdentifier ?? loanDetail?.currentEngineRateSheetIdentifier)}
                          </Typography>
                        </Stack>
                      </Popover>
                    </>
                  );

                  if (isLoanLocked) {
                    return (
                      <>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                          <Typography variant="overline" color="text.secondary">
                            <LockIcon sx={{ fontSize: 14, mr: 0.5, color: 'success.main' }} />
                            Locked
                          </Typography>
                          <Typography variant="caption" sx={{ fontWeight: isLockExpiringSoon(loan.lockExpirationDate) ? 700 : 400 }} color={isLockExpiringSoon(loan.lockExpirationDate) ? 'warning.main' : 'text.secondary'}>
                            Exp: {loan.lockExpirationDate ? new Date(loan.lockExpirationDate).toLocaleDateString() : '—'}
                            {isLockExpiringSoon(loan.lockExpirationDate) && <WarningAmberIcon sx={{ fontSize: 12, ml: 0.5 }} />}
                          </Typography>
                        </Stack>
                        {hasProduct ? (
                          <Box sx={{ fontSize: '0.8rem' }}>
                            <Typography variant="body2"><Typography component="span" color="text.secondary">Program:</Typography> <strong>{loan.programName || loan.productCode}</strong></Typography>
                            <Typography variant="body2"><Typography component="span" color="text.secondary">Rate:</Typography> <strong>{loan.noteRate != null ? `${Number(loan.noteRate).toFixed(3)}%` : '—'}</strong> <Typography component="span" color="text.secondary" sx={{ ml: 1 }}>Lock:</Typography> <strong>{loan.rateLockPeriod || '—'} days</strong></Typography>
                            {displayPrice != null && (
                              <Typography variant="body2" component="div" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                                <Typography component="span" color="text.secondary">Price:</Typography>{' '}
                                <Typography component="span" fontWeight={700} color={priceColor} sx={{ ml: 0.5 }}>{displayPrice.toFixed(3)}</Typography>
                                {priceDollars != null && <Typography component="span" color={priceColor} sx={{ ml: 0.5 }}>({priceDollars >= 0 ? '+' : ''}{priceDollars < 0 ? '−' : ''}{fmtDollar(Math.abs(priceDollars))} {priceLabel})</Typography>}
                                {pricingDetailsPopover}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">No product assigned</Typography>
                        )}
                      </>
                    );
                  }

                  return (
                    <>
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="overline" color="text.secondary">
                          <LockOpenIcon sx={{ fontSize: 14, mr: 0.5, opacity: 0.5 }} />
                          Not Locked
                        </Typography>
                      </Box>
                      {hasProduct ? (
                        <Box sx={{ fontSize: '0.8rem' }}>
                          <Typography variant="body2"><Typography component="span" color="text.secondary">Program:</Typography> <strong>{loan.programName || loan.productCode}</strong></Typography>
                          <Typography variant="body2"><Typography component="span" color="text.secondary">Rate:</Typography> <strong>{loan.noteRate != null ? `${Number(loan.noteRate).toFixed(3)}%` : '—'}</strong> <Typography component="span" color="text.secondary" sx={{ ml: 1 }}>Lock:</Typography> <strong>{loan.rateLockPeriod || '—'} days</strong></Typography>
                          {displayPrice != null && (
                            <Typography variant="body2" component="div" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                              <Typography component="span" color="text.secondary">Price:</Typography>{' '}
                              <Typography component="span" fontWeight={700} color={priceColor} sx={{ ml: 0.5 }}>{displayPrice.toFixed(3)}</Typography>
                              {priceDollars != null && <Typography component="span" color={priceColor} sx={{ ml: 0.5 }}>({priceDollars >= 0 ? '+' : ''}{priceDollars < 0 ? '−' : ''}{fmtDollar(Math.abs(priceDollars))} {priceLabel})</Typography>}
                              {pricingDetailsPopover}
                            </Typography>
                          )}
                          {/* Pricing-outdated alert lives above the tile grid; tile only carries the primary action. */}
                          {!isPricingStale && (
                            <Tooltip title={!isLockDeskEnabled ? 'The Lock Desk is currently closed — locking is paused' : ''}>
                              <span style={{ display: 'block' }}>
                                <Button size="small" variant="contained" fullWidth startIcon={<LockIcon />} disabled={!isLockDeskEnabled} onClick={() => { setLockMode(true); setActiveTab('pricing'); }} sx={{ mt: 1 }}>{isLockDeskEnabled ? 'Lock Loan' : 'Lock Desk Closed'}</Button>
                              </span>
                            </Tooltip>
                          )}
                        </Box>
                      ) : (
                        <>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>No program on this loan</Typography>
                          <Button size="small" variant="contained" fullWidth startIcon={<CalculateIcon />} onClick={() => setActiveTab('pricing')}>Run Pricing</Button>
                        </>
                      )}
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          </Grid>

          {/* 4. Stage Tile */}
          <Grid size={{ xs: 6, lg: 3 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="overline" color="text.secondary">Stage</Typography>
                <Typography variant="h6" fontWeight={700}>{loanDetail?.loan?.stage || '—'}</Typography>
                {(() => {
                  const changed = loanDetailWithKeyDates?.currentStageChangedDate;
                  if (changed) {
                    const days = Math.round((new Date() - new Date(changed)) / (1000 * 60 * 60 * 24));
                    const dayColor = days > 10 ? 'warning.main' : days > 5 ? 'info.main' : 'success.main';
                    return (
                      <Box sx={{ mt: 0.5 }}>
                        <Typography variant="h4" component="span" color={dayColor}>{days}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>day{days !== 1 ? 's' : ''} in stage</Typography>
                      </Box>
                    );
                  }
                  return loanDetail?.loan?.applicationDate
                    ? <Typography variant="caption" color="text.secondary">Applied: {new Date(loanDetail.loan.applicationDate).toLocaleDateString()}</Typography>
                    : null;
                })()}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Underwriter Decision Banner */}
        {loanDetail?.loan?.underwriterDecision && (() => {
          const UW_CFG = {
            ConditionallyApprove: { label: 'Approved with Conditions', severity: 'success', desc: 'The underwriter has conditionally approved this loan.' },
            ClearToClose:         { label: 'Clear to Close',          severity: 'success', desc: 'This loan is cleared to close — congratulations!' },
            Suspend:              { label: 'Suspended',               severity: 'warning', desc: 'The underwriter has suspended this loan pending additional information.' },
            Deny:                 { label: 'Denied',                  severity: 'error',   desc: 'The underwriter has denied this loan.' },
          };
          const d = loanDetail.loan.underwriterDecision;
          const cfg = UW_CFG[d] || { label: d, severity: 'info', desc: '' };
          const decDate = loanDetail.loan.underwriterDecisionDate;
          return (
            <Alert severity={cfg.severity} sx={{ mb: 2 }}>
              <Typography variant="subtitle2">UW Decision: {cfg.label}</Typography>
              <Typography variant="body2" color="text.secondary">
                {cfg.desc}
                {decDate && ` (${new Date(decDate).toLocaleDateString()})`}
              </Typography>
            </Alert>
          );
        })()}

        {/* Tabs */}
        <Card>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider', px: 1 }}
          >
            <Tab value="loan" icon={<AccountBalanceIcon fontSize="small" />} iconPosition="start" label={<>Loan{tabErrorDot('loan')}{isEditMode && <DirtyBadge tabKey="loan" control={control} />}</>} />
            {isGovernmentLoan && <Tab value="government" icon={<GavelIcon fontSize="small" />} iconPosition="start" label={isEditMode ? (editableMortgageType || 'Govt') : (loanDetail?.loan?.mortgageType || 'Govt')} />}
            {showNonQmTab && (
              <Tab value="nonqm" icon={<DatasetLinkedIcon fontSize="small" />} iconPosition="start" label="Non-QM" />
            )}
            <Tab value="pricing" icon={<CalculateIcon fontSize="small" />} iconPosition="start" label="Pricing" />
            <Tab value="borrower" icon={<PersonIcon fontSize="small" />} iconPosition="start" label={<>Borrower{tabErrorDot('borrower')}{isEditMode && <DirtyBadge tabKey="borrower" control={control} />}</>} />
            <Tab value="income" icon={<WorkIcon fontSize="small" />} iconPosition="start" label={<>Income{tabErrorDot('income')}{isEditMode && <DirtyBadge tabKey="income" control={control} />}</>} />
            <Tab value="property" icon={<HomeWorkIcon fontSize="small" />} iconPosition="start" label={<>Property{tabErrorDot('property')}{isEditMode && <DirtyBadge tabKey="property" control={control} />}</>} />
            <Tab value="credit" icon={<CreditScoreIcon fontSize="small" />} iconPosition="start" label={<>Credit{tabErrorDot('credit')}{isEditMode && <DirtyBadge tabKey="credit" control={control} />}</>} />
            <Tab value="assets" icon={<SavingsIcon fontSize="small" />} iconPosition="start" label={<>Assets{isEditMode && <DirtyBadge tabKey="assets" control={control} />}</>} />
            <Tab value="fees" icon={<ReceiptLongIcon fontSize="small" />} iconPosition="start" label="Fees" />
            <Tab value="documents" icon={<DescriptionIcon fontSize="small" />} iconPosition="start" label="Docs" />
            <Tab value="conditions" icon={<ChecklistIcon fontSize="small" />} iconPosition="start" label="Conditions" />
            {showDocOrderTab && (
              <Tab value="docorder" icon={<FactCheckOutlinedIcon fontSize="small" />} iconPosition="start" label="Doc Order" />
            )}
            <Tab value="keydates" icon={<ViewTimelineIcon fontSize="small" />} iconPosition="start" label="Timeline" />
            {env.p2enabled && <Tab value="activity" icon={<TimelineIcon fontSize="small" />} iconPosition="start" label="Activity" />}
            {env.p2enabled && (
              <Tab value="messages" icon={<ChatIcon fontSize="small" />} iconPosition="start" label={
                <Badge badgeContent={chatThreads.reduce((sum, t) => sum + t.unreadCount, 0)} color="error" max={99}>
                  Messages
                </Badge>
              } />
            )}
            {env.p2enabled && <Tab value="aus" icon={<CompareIcon fontSize="small" />} iconPosition="start" label="AUS" />}
            {isLoanLocked && !blockBrokerReadOnlyActions && <Tab value="postlock" icon={<LockIcon fontSize="small" />} iconPosition="start" label="Post-Lock Actions" />}
            {isReadOnly && !blockBrokerReadOnlyActions && (
              <Tab value="coc" icon={<SwapHorizIcon fontSize="small" />} iconPosition="start" label={
                <Badge badgeContent={cocRequests.filter(r => r.status === 'Submitted' || r.status === 'Under Review').length} color="warning" max={99}>
                  COC
                </Badge>
              } />
            )}
          </Tabs>

          <Box sx={{ p: 2 }}>
            {/* Borrower Tab (URLA Section 1a) */}
            {activeTab === 'borrower' && (
              <div>
                {!isReadOnly && !blockBrokerReadOnlyActions && isFeatureEnabled('orderVOIEButton') && (
                  <Box sx={{ mb: 2 }}>
                    <Button variant="contained" color="success" onClick={() => setShowOrderVOIEModal(true)}>
                      Order Digital Income/Employment Verification (VOIE)
                    </Button>
                  </Box>
                )}
                <BorrowerInfoTab
                  loanDetail={loanDetail}
                  isEditMode={isEditMode}
                />
              </div>
            )}

            {/* Income Tab (URLA Section 1b/1c) */}
            {activeTab === 'income' && (
              <IncomeEmploymentTab
                loanDetail={loanDetail}
                isEditMode={isEditMode}
              />
            )}

            {/* Property Tab */}
            {activeTab === 'property' && (
              <div>
                {!isReadOnly && !blockBrokerReadOnlyActions && (isFeatureEnabled('orderAppraisalButton') || isFeatureEnabled('openTitleButton')) && (
                  <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    {isFeatureEnabled('orderAppraisalButton') && (
                      <Button variant="contained" onClick={() => setShowOrderAppraisalModal(true)}>Order Appraisal</Button>
                    )}
                    {isFeatureEnabled('openTitleButton') && (
                      <Button variant="contained" color="success" onClick={() => setShowOpenTitleModal(true)}>Open Title Order</Button>
                    )}
                  </Stack>
                )}
                <PropertyTab loanDetail={loanDetail} isEditMode={isEditMode} />
              </div>
            )}

            {/* Loan Details Tab */}
            {activeTab === 'loan' && (
              <LoanTab
                loanDetail={loanDetail}
                isEditMode={isEditMode}
                onMortgageTypeChange={handleMortgageTypeChange}
                onNavigateToPricing={(mortgageType) => {
                  const fams = [];
                  if (mortgageType) {
                    const family = resolveMortgageTypeToPricingFamily(mortgageType);
                    if (family) fams.push(family);
                  }
                  const isNonQmNow =
                    loanDetail?.loan?.nonQualifiedMortgageIndicator === true
                    || watchedNonQmIndicator === true
                    || watchedNonQmIndicator === 'True';
                  if (isNonQmNow && !fams.includes('Non-QM')) {
                    fams.push('Non-QM');
                  }
                  if (fams.length > 0) {
                    setStalePricingFilters({ selectedFamilies: fams, allProducts: true });
                  }
                  setActiveTab('pricing');
                  if (isEditMode) {
                    handleEditToggle();
                  }
                }}
              />
            )}

            {/* Government Tab (FHA / VA / USDA) */}
            {activeTab === 'government' && isGovernmentLoan && (
              <GovernmentTab loanDetail={loanDetail} isEditMode={isEditMode} />
            )}

            {activeTab === 'nonqm' && showNonQmTab && (
              <NonQmTab nonQm={loanDetail?.nonQm} loan={loanDetail?.loan} isEditMode={isEditMode} />
            )}

            {/* Credit / Liabilities Tab */}
            {activeTab === 'credit' && (
              <div>
                {!isReadOnly && !blockBrokerReadOnlyActions && isFeatureEnabled('pullCreditButton') && (
                  <Box sx={{ mb: 2 }}>
                    <Button variant="contained" onClick={() => setShowPullCreditModal(true)}>Pull Credit Report</Button>
                  </Box>
                )}
                <CreditLiabilitiesTab loanDetail={loanDetail} isEditMode={isEditMode} />
              </div>
            )}

            {/* Assets Tab */}
            {activeTab === 'assets' && (
              <div>
                {!isReadOnly && !blockBrokerReadOnlyActions && isFeatureEnabled('orderVOAButton') && (
                  <Box sx={{ mb: 2 }}>
                    <Button variant="contained" color="success" onClick={() => setShowOrderVOAModal(true)}>Order Digital Asset Verification (VOA)</Button>
                  </Box>
                )}
                <AssetsTab loanDetail={loanDetail} isEditMode={isEditMode} />
              </div>
            )}

            {/* Pricing Tab — keep mounted whenever loan is loaded so lock-mode / pricing state stay warm without switching tabs */}
            {loanDetail && id && (
              <Box sx={{ display: activeTab === 'pricing' ? 'block' : 'none' }} aria-hidden={activeTab !== 'pricing'}>
                {showAdvisorBanner && isFeatureEnabled('mismoImportAdvisor') && (
                  <Alert
                    severity="success"
                    sx={{ mb: 2 }}
                    onClose={() => setShowAdvisorBanner(false)}
                  >
                    <Typography variant="subtitle2" fontWeight={700}>Product assigned from MISMO advisor</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Review current pricing below. You can lock the rate, submit to UFF, or upload documents when ready.
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<LockIcon />}
                        disabled={blockBrokerReadOnlyActions || isLoanLocked || !isLockDeskEnabled}
                        title={!isLockDeskEnabled ? 'The Lock Desk is currently closed — locking is paused' : ''}
                        onClick={() => { setLockMode(true); setActiveTab('pricing'); }}
                      >
                        Lock rate
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={blockBrokerReadOnlyActions || isSubmitting}
                        onClick={handleSubmitToUFF}
                      >
                        Submit to UFF
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => { setShowAdvisorBanner(false); setActiveTab('documents'); }}
                      >
                        Upload documents
                      </Button>
                    </Stack>
                  </Alert>
                )}
                <PricingTab
                  loanDetail={loanDetail}
                  loanId={id}
                  pricingRequest={loanDetailWrapper?.pricingRequest}
                  brokerCompany={loanDetailWrapper?.brokerCompany}
                  isReadOnly={isReadOnly || blockBrokerReadOnlyActions}
                  hasUnsavedChanges={isEditMode && isDirty}
                  initialFilters={stalePricingFilters || pricingFilters}
                  autoRun={autoRunPricing}
                  onAutoRunComplete={() => setAutoRunPricing(false)}
                  lockMode={lockMode}
                  onLockModeComplete={() => setLockMode(false)}
                  onNavigateTab={setActiveTab}
                  showAdvisorQuickActions={showAdvisorBanner && isFeatureEnabled('mismoImportAdvisor')}
                />
              </Box>
            )}

            {/* Documents Tab */}
            {activeTab === 'documents' && (
              <DocumentsTab loanId={id} disableMutations={blockBrokerReadOnlyActions} />
            )}

            {/* Conditions Tab */}
            {activeTab === 'conditions' && (
              <ConditionsTab loanId={id} />
            )}

            {activeTab === 'docorder' && showDocOrderTab && (
              <DocOrderTab docOrder={loanDetail.docOrder} loan={loanDetail.loan} loanId={id} />
            )}

            {/* Timeline — velocity, process timeline (LE/CD from API), detailed key dates */}
            {activeTab === 'keydates' && loanDetailWithKeyDates && (
              <div>
                <LoanVelocityTiles loanDetail={loanDetailWithKeyDates} />
                <LoanTimelineTab loanDetail={loanDetail} keyDatesObj={loanDetailWithKeyDates.keyDatesObj} />
                <Box sx={{ mt: 3 }}>
                  <SectionHeader
                    title="Additional milestone dates"
                    icon={<CalendarMonthIcon />}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Expanded checklist of lock, TRID, appraisal, title, and other milestones. Use the timeline above for disclosure delivery detail and pacing.
                  </Typography>
                  <KeyDatesCard loanDetail={loanDetailWithKeyDates} />
                </Box>
              </div>
            )}

            {/* Activity Timeline Tab */}
            {env.p2enabled && activeTab === 'activity' && (
              <div>
                <Typography variant="h6" sx={{ mb: 2 }}>Activity Timeline</Typography>
                <List disablePadding>
                  {activities.map(activity => {
                    const chipColor =
                      activity.type.includes('uploaded') ? 'info' :
                      activity.type.includes('satisfied') || activity.type.includes('signed') ? 'success' :
                      activity.type.includes('rejected') ? 'error' :
                      activity.type.includes('submitted') ? 'primary' : 'default';
                    return (
                      <ListItem key={activity.id} divider sx={{ alignItems: 'flex-start' }}>
                        <ListItemText
                          primary={<Typography variant="subtitle2">{activity.user}</Typography>}
                          secondary={
                            <>
                              <Typography variant="body2" color="text.secondary">{activity.description}</Typography>
                              <Typography variant="caption" color="text.secondary">{new Date(activity.timestamp).toLocaleString()}</Typography>
                            </>
                          }
                        />
                        <Chip label={activity.type.replace(/_/g, ' ')} size="small" color={chipColor} variant="outlined" />
                      </ListItem>
                    );
                  })}
                </List>
              </div>
            )}

            {/* AUS Tab */}
            {activeTab === 'aus' && env.p2enabled && (
              <div>
                <DualAUSComponent
                  loan={loanDetail?.loan}
                  onAUSComplete={(selectedAUS, results) => {
                    console.log('AUS selected:', selectedAUS, results);
                  }}
                />
              </div>
            )}

            {/* Post-Lock Actions Tab */}
            {activeTab === 'postlock' && isLoanLocked && !blockBrokerReadOnlyActions && (
              <PostLockActionsTab loanDetail={loanDetail} />
            )}

            {/* Fees & LE Tab */}
            {activeTab === 'fees' && (
              <div>
                <FeesWorksheetComponent loanId={id} loanDetail={loanDetail} isReadOnly={isReadOnly || blockBrokerReadOnlyActions} onNavigateToPricing={() => setActiveTab('pricing')} />
              </div>
            )}

            {/* COC Tab */}
            {activeTab === 'coc' && !blockBrokerReadOnlyActions && (
              <div>
                {showCOCForm ? (
                  <COCRequestForm
                    loan={loanDetail?.loan}
                    onSubmit={handleCOCSubmit}
                    onCancel={() => setShowCOCForm(false)}
                  />
                ) : (
                  <>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                      <Box>
                        <Typography variant="h6">Change of Circumstance (COC)</Typography>
                        <Typography variant="body2" color="text.secondary">
                          TRID-compliant COC requests for lender review
                        </Typography>
                      </Box>
                      <Button variant="contained" onClick={() => setShowCOCForm(true)}>
                        New COC Request
                      </Button>
                    </Stack>
                    <COCRequestList
                      cocRequests={cocRequests}
                      onViewRequest={(request) => setSelectedCOCRequest(request)}
                    />
                  </>
                )}
              </div>
            )}

            {/* Messages Tab */}
            {env.p2enabled && activeTab === 'messages' && (
              <Grid container spacing={2}>
                {/* Thread Sidebar */}
                <Grid size={{ xs: 12, md: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>Threads</Typography>
                  <List disablePadding>
                    {chatThreads.map(thread => (
                      <ListItem
                        key={thread.id}
                        button
                        selected={activeThread === thread.id}
                        onClick={() => setActiveThread(thread.id)}
                        divider
                      >
                        <ListItemText
                          primary={thread.name}
                          secondary={thread.lastMessage}
                          secondaryTypographyProps={{ noWrap: true }}
                        />
                        {thread.unreadCount > 0 && (
                          <Chip label={thread.unreadCount} size="small" color="error" variant="outlined" />
                        )}
                      </ListItem>
                    ))}
                  </List>
                </Grid>

                {/* Chat Area */}
                <Grid size={{ xs: 12, md: 9 }}>
                  <Card>
                    <CardHeader
                      title={chatThreads.find(t => t.id === activeThread)?.name || 'Messages'}
                      subheader={chatThreads.find(t => t.id === activeThread)?.description}
                      titleTypographyProps={{ variant: 'subtitle1' }}
                    />
                    <CardContent sx={{ height: 500, overflowY: 'auto' }}>
                      <List disablePadding>
                        {chatMessages
                          .filter(msg => msg.threadId === activeThread)
                          .map(message => (
                            <ListItem key={message.id} sx={{ px: 0, alignItems: 'flex-start' }}>
                              {message.messageType === 'system' ? (
                                <Alert severity="info" sx={{ width: '100%' }}>
                                  <Typography variant="body2" fontWeight={700}>{message.message}</Typography>
                                  {message.systemEventType === 'lock' && message.metadata && (
                                    <Typography variant="caption" color="text.secondary">
                                      Rate: {message.metadata.rate}% | Period: {message.metadata.lockPeriod} days | Expires: {new Date(message.metadata.expirationDate).toLocaleDateString()} | Confirmation: {message.metadata.confirmation}
                                    </Typography>
                                  )}
                                  {message.systemEventType === 'approval' && message.metadata && (
                                    <Typography variant="caption" color="text.secondary">
                                      Type: {message.metadata.approvalType} | Conditions: {message.metadata.conditions.join(', ')} | By: {message.metadata.approvedBy}
                                    </Typography>
                                  )}
                                  {message.systemEventType === 'ctc' && message.metadata && (
                                    <Typography variant="caption" color="text.secondary">
                                      Closing Date: {new Date(message.metadata.closingDate).toLocaleDateString()} | Cleared By: {message.metadata.clearedBy}
                                    </Typography>
                                  )}
                                  <Typography variant="caption" color="text.secondary" display="block">{new Date(message.timestamp).toLocaleString()}</Typography>
                                </Alert>
                              ) : (
                                <Stack direction={message.senderType === 'broker' ? 'row-reverse' : 'row'} spacing={1} sx={{ width: '100%' }}>
                                  <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: message.senderType === 'broker' ? 'info.light' : 'primary.light', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', flexShrink: 0 }}>
                                    {message.senderAvatar}
                                  </Box>
                                  <Box sx={{ maxWidth: '70%', textAlign: message.senderType === 'broker' ? 'right' : 'left' }}>
                                    <Typography variant="caption" fontWeight={700}>
                                      {message.senderName}
                                      {message.senderCompany && <Typography component="span" variant="caption" color="text.secondary"> • {message.senderCompany}</Typography>}
                                    </Typography>
                                    <Card variant="outlined" sx={{ mt: 0.5, bgcolor: message.senderType === 'broker' ? 'rgba(var(--mui-palette-info-mainChannel) / 0.10)' : 'rgba(var(--mui-palette-primary-mainChannel) / 0.10)' }}>
                                      <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{message.message}</Typography>
                                        {message.attachments?.length > 0 && (
                                          <Stack spacing={0.5} sx={{ mt: 1 }}>
                                            {message.attachments.map(file => (
                                              <Stack key={file.id} direction="row" alignItems="center" spacing={0.5}>
                                                <AttachFileIcon sx={{ fontSize: 14 }} />
                                                <Link href="#" underline="hover" variant="caption">{file.name}</Link>
                                                <Typography variant="caption" color="text.secondary">({Math.round(file.size / 1024)} KB)</Typography>
                                              </Stack>
                                            ))}
                                          </Stack>
                                        )}
                                      </CardContent>
                                    </Card>
                                    <Typography variant="caption" color="text.secondary">
                                      {new Date(message.timestamp).toLocaleString()}
                                      {!message.isRead && message.senderType === 'lender' && <Chip label="New" size="small" color="error" variant="outlined" sx={{ ml: 0.5, height: 16 }} />}
                                    </Typography>
                                  </Box>
                                </Stack>
                              )}
                            </ListItem>
                          ))}
                      </List>
                    </CardContent>
                    <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                      {attachedFiles.length > 0 && (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: 1 }}>
                          {attachedFiles.map((file, idx) => (
                            <Chip key={idx} icon={<AttachFileIcon />} label={file.name} size="small" onDelete={() => setAttachedFiles(attachedFiles.filter((_, i) => i !== idx))} />
                          ))}
                        </Stack>
                      )}
                      <Stack direction="row" spacing={1} alignItems="flex-end">
                        <IconButton size="small" onClick={() => { setAttachedFiles([...attachedFiles, { name: 'document.pdf', size: 245000 }]); }} title="Attach file">
                          <AttachFileIcon />
                        </IconButton>
                        <TextField
                          fullWidth
                          multiline
                          minRows={2}
                          maxRows={4}
                          size="small"
                          placeholder="Type your message..."
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              if (newMessage.trim()) { showToast('Message sent!'); setNewMessage(''); setAttachedFiles([]); }
                            }
                          }}
                        />
                        <IconButton color="primary" onClick={() => { if (newMessage.trim()) { showToast('Message sent!'); setNewMessage(''); setAttachedFiles([]); } }}>
                          <SendIcon />
                        </IconButton>
                      </Stack>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>Press Enter to send, Shift+Enter for new line</Typography>
                    </Box>
                  </Card>
                </Grid>
              </Grid>
            )}
          </Box>
        </Card>

        {/* AI Chat Panel (real streaming) */}
        {isFeatureEnabled('loanAIchatEnabled') && id && !blockBrokerReadOnlyActions && (
          <AIChatPanel
            loanId={id}
            isOpen={isAIChatOpen}
            onToggle={() => setIsAIChatOpen(prev => !prev)}
            quickActionsEnabled={aiQuickActionsEnabled}
            scenarioEnabled={aiScenarioEnabled}
            feedbackEnabled={aiFeedbackEnabled}
          />
        )}

        {/* Self-Service Modals */}
        <PullCreditModal
          show={showPullCreditModal}
          onClose={() => setShowPullCreditModal(false)}
          onSubmit={handlePullCredit}
          borrowerName={`${loanDetail?.borrowers?.[0]?.firstName || ''} ${loanDetail?.borrowers?.[0]?.lastName || ''}`.trim()}
        />

        <OrderAppraisalModal
          show={showOrderAppraisalModal}
          onClose={() => setShowOrderAppraisalModal(false)}
          onSubmit={handleOrderAppraisal}
          property={{
            address: loanDetail?.property?.address?.street || '',
            propertyType: loanDetail?.property?.propertyType,
            occupancy: loanDetail?.property?.occupancy,
            estimatedValue: loanDetail?.property?.estimatedValue
          }}
        />

        <OpenTitleModal
          show={showOpenTitleModal}
          onClose={() => setShowOpenTitleModal(false)}
          onSubmit={handleOpenTitle}
          property={{ address: loanDetail?.property?.address?.street || '' }}
          borrower={{ name: `${loanDetail?.borrowers?.[0]?.firstName || ''} ${loanDetail?.borrowers?.[0]?.lastName || ''}`.trim() }}
        />

        <OrderVOAModal
          show={showOrderVOAModal}
          onClose={() => setShowOrderVOAModal(false)}
          onSubmit={handleOrderVOA}
          borrowerName={`${loanDetail?.borrowers?.[0]?.firstName || ''} ${loanDetail?.borrowers?.[0]?.lastName || ''}`.trim()}
        />

        <OrderVOIEModal
          show={showOrderVOIEModal}
          onClose={() => setShowOrderVOIEModal(false)}
          onSubmit={handleOrderVOIE}
          borrowerName={`${loanDetail?.borrowers?.[0]?.firstName || ''} ${loanDetail?.borrowers?.[0]?.lastName || ''}`.trim()}
          employer={getCurrentEmployer(loanDetail?.borrowers?.[0])}
        />

        {/* COC Request Detail Modal */}
        {selectedCOCRequest && (
          <COCRequestDetail
            request={selectedCOCRequest}
            onClose={() => setSelectedCOCRequest(null)}
          />
        )}
      </div>
    </div>
    </FormProvider>
  );
};

export default LoanDetailPage;
